import { yupResolver } from "@hookform/resolvers/yup";
import * as Device from "expo-device";
import { useRouter } from "expo-router";
import {
  type ComponentRef,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import {
  Keyboard,
  NativeSyntheticEvent,
  Platform,
  TextInputSelectionChangeEventData,
  useWindowDimensions,
} from "react-native";
import { useDebouncedCallback } from "use-debounce";
import * as yup from "yup";

import { useAuth } from "../../providers/Auth";
import ConfirmCodeView, { IForm } from "./ConfirmCodeView";

import { AnalyticsTracker } from "@/features/analytics";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { TextInput } from "@/components/atoms/TextInput";
import {
  ConfirmSignInResult,
  SignInResult,
  useAuthConfirmSignInMutation,
  useAuthResendVerifyIdentifierMutation,
} from "@/generated/graphql";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";
import useSetPageTitle from "@/hooks/useSetPageTitle";
import { useToastNotification } from "@/hooks/useToastNotification";
import { useTheme } from "@/providers/ThemeProvider";
import { formatError } from "@/utils/formatError";
import { ifWeb } from "@/utils/platform";

const CODE_LENGTH = 6;

/**
 * Inline copy shown when a code is rejected and the backend supplied no
 * message of its own (SE-6887).
 *
 * The frontend owns neither the wording nor the branch that produces it — the
 * "You have N attempts left" sentence comes from one arm of
 * `respondToAuthChallenge`, an invalid/expired session from another. This
 * string exists so the inline region is never empty, because
 * `FormControlError` renders nothing without children and an error with no
 * message would silently be no error at all.
 */
const CODE_REJECTED_FALLBACK = "That code wasn't accepted. Please try again.";

/**
 * NativeWind `md:` breakpoint (768px). The auth content panel fixes its width
 * at md+ and otherwise fills the viewport — a screen-width decision derived
 * from the measured window width rather than the vendored Gluestack breakpoint hook.
 */
const MD_BREAKPOINT = 768;

const schema = yup.object().shape({
  code: yup
    .string()
    .matches(/^\d+$/, "OTP must contain only numbers")
    .length(CODE_LENGTH, `OTP must be exactly ${CODE_LENGTH} digits`)
    .required("Can't be blank"),
});

/**
 * Tracks the cursor position in the OTP input field for visual focus indication.
 */
interface IPositionCursor {
  start: number;
  end: number;
}

/**
 * Handles OTP verification during phone-based authentication flow.
 * Manages code input, validation, auto-submission, and resend functionality with rate limiting.
 * @module features/auth/screens/ConfirmCode
 */
const ConfirmCodeContainer = () => {
  const inputRef = useRef<ComponentRef<typeof TextInput>>(null) as RefObject<
    ComponentRef<typeof TextInput>
  >;
  const codeRef = useRef("");
  const [positionCursor, setPositionCursor] = useState<
    IPositionCursor | undefined
  >(undefined);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors, isValid, isSubmitting },
  } = useForm<IForm>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      code: "",
    },
    delayError: 1000,
  });
  const toast = useToastNotification();
  const auth = useAuth();
  const userGoal = useRecordUserGoal({
    goalType: GoalType.ConfirmSignIn,
    value: "",
  });
  const resendOTPGoal = useRecordUserGoal({
    goalType: GoalType.ResendOTP,
    value: "",
  });
  const [confirmSignIn] = useAuthConfirmSignInMutation({
    onCompleted: () => {
      userGoal();
    },
  });
  const [resendOTP] = useAuthResendVerifyIdentifierMutation({
    onCompleted: () => {
      resendOTPGoal();
    },
  });
  const router = useRouter();

  const submit = useCallback(
    async ({ code }: IForm) => {
      Keyboard.dismiss();
      try {
        const result = await confirmSignIn({
          variables: {
            input: {
              otpCode: code,
              session: auth?.data?.session ?? "",
              identifier: `${auth?.data?.identifier ?? ""}`,
            },
          },
        });

        if (result.data?.confirmSignIn?.errorMessage?.message) {
          toast.show({
            description: result.data?.confirmSignIn.errorMessage.message,
            action: "danger",
          });
        }

        if (result.data?.confirmSignIn?.signInResult?.message) {
          toast.show({
            description: result.data?.confirmSignIn.signInResult.message,
            action: "danger",
          });
        }

        // SE-6887: `handleCompleteConfirmSignIn` has already collapsed every
        // backend branch into one shape — `success` plus a normalized
        // `message`. The signInResult arm (wrong code, retry allowed) and the
        // errorMessage arm (invalid/expired session) both come back as
        // `success: false`, and the latter already defaults its own copy. So
        // the inline error is driven by the OUTCOME, never by matching a
        // sentence: a message this client has never seen still lands here, and
        // a branch that supplies no message at all still raises the region.
        const { success, message } = await auth!.onCompleteConfirmSignIn(
          result.data?.confirmSignIn! as ConfirmSignInResult
        );

        reset({ code: "" });
        codeRef.current = "";

        if (!success) {
          // Must follow `reset`, which clears form errors. This is the durable
          // half of the rejection: the toast above evaporates on a timer and
          // the cells have just been emptied, so without this a user who looks
          // away comes back to a blank screen that says nothing went wrong.
          setError("code", {
            type: "server",
            message: message ?? CODE_REJECTED_FALLBACK,
          });
        }

        // Track login analytics with success status from auth response
        AnalyticsTracker.user.logUserSignIn({
          method: "phone_otp",
          phone_number: auth?.data?.identifier ?? "",
          device_name: Device?.modelName ?? "",
          device_os: Device?.osName ?? "",
          success,
          device_type: ifWeb("web", "mobile"),
          platform: Platform.OS,
          timestamp: new Date().toISOString(),
        });

        if (success) {
          // Identify user in PostHog for cross-session tracking
          const phoneNumber = auth?.data?.identifier ?? "";
          if (phoneNumber) {
            AnalyticsTracker.identifyUser(phoneNumber, {
              phone_number: phoneNumber,
            });
          }
          router.replace("/");
        }
      } catch (error) {
        console.error("Error during sign in confirmation:", error);

        // Tell the user. Without this the failure is completely silent: the
        // two toasts above only fire when the mutation RETURNS a structured
        // errorMessage, so anything that THROWS — network, GraphQL, or
        // onCompleteConfirmSignIn — left a real person looking at a re-armed
        // Verify button with their code still in the boxes and no indication
        // that anything had gone wrong.
        //
        // The digits survive precisely because this branch was taken:
        // `reset({ code: "" })` sits on the success path above, before the
        // `success` check, so it never runs when we land here.
        //
        // Mirrors SignInContainer's catch, which already does this for the
        // phone step — the two halves of the same journey disagreed.
        const thrownMessage =
          formatError(error)?.message ?? "Something went wrong";

        toast.show({
          description: thrownMessage,
          action: "danger",
        });

        // SE-6887: the third rejection path, and the one with no backend copy
        // at all. The digits survive here (see above), so the inline error
        // sits under a filled-in code until the user changes it.
        setError("code", { type: "server", message: thrownMessage });

        // Track failed login attempt
        AnalyticsTracker.user.logUserSignIn({
          method: "phone_otp",
          phone_number: auth?.data?.identifier ?? "",
          device_name: Device?.modelName ?? "",
          device_os: Device?.osName ?? "",
          success: false,
          device_type: ifWeb("web", "mobile"),
          platform: Platform.OS,
          timestamp: new Date().toISOString(),
        });
      }
    },
    [auth, confirmSignIn, reset, router, setError, toast]
  );
  // eslint-disable-next-line react-hooks/refs -- submit callback runs on form submission (user action), not during render
  const doSubmit = handleSubmit(submit);

  const handleResendOTP = useCallback(async () => {
    try {
      const responseResendOTP = await resendOTP({
        variables: { input: { identifier: `${auth?.data?.identifier ?? ""}` } },
      });
      reset({ code: "" });
      codeRef.current = "";
      setPositionCursor(undefined);
      await auth?.onCompleteSignIn(
        responseResendOTP.data?.resendOTP as SignInResult,
        `${auth?.data?.identifier ?? ""}`
      );
      toast.show({ description: "Code sent", action: "success" });
    } catch (error) {
      const { message, errorCode } = formatError(error);
      if (errorCode === "OTP_SPAM_PREVENTION_EXCEPTION") {
        reset({ code: "" });
        codeRef.current = "";
        setPositionCursor(undefined);
      }
      toast.show({
        description: message ?? "There has been an error, please try again",
        action: "danger",
      });
    }
  }, [auth, resendOTP, reset, toast]);
  const debounceResendOTP = useDebouncedCallback(handleResendOTP, 300);

  // Focus the hidden code field, but only when it is not already the first
  // responder.
  //
  // SE-6816: the unguarded `focus()` this replaces ran on EVERY keystroke —
  // and again from `onFocus`, which routes back here, so the two fed each
  // other. Re-arming an already-focused TextInput is not free on iOS: UIKit
  // tears the keyboard input session down and rebuilds it, and keystrokes
  // that arrive while that round trip is in flight reach a stale responder
  // and are dropped.
  //
  // Measured on the iOS Maestro simulator, run 32497084870 (auth/logout):
  // the first digit landed at 15:55:32.118, the app immediately emitted two
  // `setWindowContextID … windowState:Enabled` keyboard-arbiter round trips
  // (15:55:32.129 / .137), and `signalKeyboardChangeComplete` did not come
  // back until 15:55:34.202 — 2.07s. All five remaining key events
  // (15:55:32.834–.966) were delivered into that window and none reached the
  // field: the screen kept one digit and this screen's own "OTP must be
  // exactly 6 digits" error. The healthy pass later in the same run closed
  // the identical round trip in 22ms and kept all six digits, and the phone
  // field one screen earlier typed ten characters with zero arbiter churn
  // and lost nothing — the churn is this handler, not the platform.
  //
  // Guarding on `isFocused()` keeps the recovery this call exists for (the
  // field genuinely having lost focus) and drops the redundant re-arm.
  const focusCodeInput = useCallback(() => {
    if (inputRef.current?.isFocused()) {
      return;
    }
    inputRef.current?.focus();
  }, []);

  const handleChange = useCallback(
    (text: string) => {
      const sanitizedText = text.replace(/\D/g, "").slice(0, CODE_LENGTH);

      // SE-6887: a new attempt retires the previous rejection. This has to be
      // explicit. The form runs with `delayError: 1000`, and that debounce
      // holds the PREVIOUS error in place while the next one is pending — so
      // typing would leave the stale server message on screen for a further
      // second rather than clearing it. Clearing here is an event handler, not
      // an effect, so it is not the setState-in-effect shape the repo forbids.
      if (sanitizedText !== codeRef.current) {
        clearErrors("code");
      }

      codeRef.current = sanitizedText;

      if (sanitizedText.length === CODE_LENGTH) {
        doSubmit();
      }

      focusCodeInput();
    },
    [clearErrors, doSubmit, focusCodeInput]
  );

  const handleSelectionChange = useCallback(
    (e: NativeSyntheticEvent<TextInputSelectionChangeEventData>) =>
      setPositionCursor(e.nativeEvent.selection),
    []
  );

  const handlePress = focusCodeInput;

  const boxArray = useMemo(() => new Array(CODE_LENGTH).fill(0), []);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const boxDigit = useCallback(
    (_: unknown, index: number) => {
      const digit = codeRef.current[index] || "";
      const isFocused = positionCursor
        ? positionCursor?.start === index
        : codeRef.current.length === index;

      return (
        <Box
          key={index}
          className={`flex h-12 min-w-0 max-w-14 flex-1 items-center justify-center rounded-none border-2 ${isFocused ? "border-needs-semantic-primary-500" : "border-transparent"} ${isDark ? "bg-surface-action" : "bg-surface-subtle"}`}
        >
          <Text
            variant="unstyled"
            className={`${ifWeb("text-title", "text-title-lg")} text-status-info`}
          >
            {digit}
          </Text>
        </Box>
      );
    },
    [positionCursor, isDark]
  );

  const { width } = useWindowDimensions();
  const widthContent = width >= MD_BREAKPOINT ? 750 : width;

  useSetPageTitle("Confirm Code");

  useEffect(() => {
    setTimeout(focusCodeInput, 500);
  }, [focusCodeInput]);

  return (
    <ConfirmCodeView
      valid={isValid}
      loading={isSubmitting}
      widthContent={widthContent}
      control={control}
      errors={errors}
      onSubmit={doSubmit}
      onPress={handlePress}
      onChange={handleChange}
      onSelectionChange={handleSelectionChange}
      inputRef={inputRef}
      boxArray={boxArray}
      boxDigit={boxDigit}
      onResendOTP={debounceResendOTP}
      isDark={isDark}
    />
  );
};

export default ConfirmCodeContainer;
